import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Conversations.css';

const Conversations = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('user'));
  const token = localStorage.getItem('token');
  const base_url = 'https://api.homehive.co.zw/api';
  const pollIntervalRef = useRef(null);
  const [lastUpdate, setLastUpdate] = useState(new Date().getTime());
  const [totalUnread, setTotalUnread] = useState(0);
  const lastTotalUnreadRef = useRef(0);
  const audioRef = useRef(new Audio('/notification.mp3')); // Add notification sound file

  // Authentication check
  useEffect(() => {
    if (!currentUser || !token) {
      navigate('/login');
      return;
    }

    if (currentUser.user.is_landlord) {
      navigate('/landlord/conversations');
      return;
    }
  }, [currentUser, token, navigate]);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(
        `${base_url}/conversations/`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      const sortedConversations = response.data.sort((a, b) => {
        const timeA = a.last_message?.timestamp ? new Date(a.last_message.timestamp) : new Date(0);
        const timeB = b.last_message?.timestamp ? new Date(b.last_message.timestamp) : new Date(0);
        return timeB - timeA;
      });

      setConversations(sortedConversations);
      
      // Calculate total unread messages
      const newTotalUnread = sortedConversations.reduce(
        (total, conv) => total + (conv.unread_count || 0), 
        0
      );
      
      // Check if there are new unread messages
      if (newTotalUnread > lastTotalUnreadRef.current) {
        const diff = newTotalUnread - lastTotalUnreadRef.current;
        toast.info(`You have ${diff} new message${diff > 1 ? 's' : ''}!`);
        audioRef.current.play().catch(console.error);
      }

      lastTotalUnreadRef.current = newTotalUnread;
      setTotalUnread(newTotalUnread);
      setLastUpdate(new Date().getTime());
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setError('Failed to load conversations');
    }
  };

  // Initial load
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        await fetchConversations();
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setError('Failed to load initial data');
      }
    };

    if (token) {
      loadInitialData();
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [token]);

  // Improved polling mechanism
  useEffect(() => {
    let isSubscribed = true;

    const startPolling = () => {
      // Clear any existing interval
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }

      // Set up new polling interval
      pollIntervalRef.current = setInterval(async () => {
        if (!isSubscribed || !token) return;

        try {
          const response = await axios.get(
            `${base_url}/conversations/check-updates`,
            {
              headers: { 'Authorization': `Bearer ${token}` },
              params: { last_update: lastUpdate }
            }
          );

          if (response.data?.hasUpdates) {
            await fetchConversations();
          }
        } catch (error) {
          if (error.response?.status === 401) {
            clearInterval(pollIntervalRef.current);
            navigate('/login');
          } else {
            console.error('Polling error:', error);
          }
        }
      }, 3000);
    };

    if (token) {
      startPolling();
    }

    // Cleanup function
    return () => {
      isSubscribed = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [token, lastUpdate, navigate]);

  const getOtherParticipant = (conversation) => {
    return conversation.participants.find(p => p.id !== currentUser.user.id);
  };

  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    // Less than 24 hours
    if (diff < 24 * 60 * 60 * 1000) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    // Less than 7 days
    if (diff < 7 * 24 * 60 * 60 * 1000) {
      return date.toLocaleDateString([], { weekday: 'short' });
    }
    // Otherwise
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="conversations-container">
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i> Loading conversations...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="conversations-container">
        <div className="error-message">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      </div>
    );
  }

  return (
    <div className="conversations-container">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      
      <div className="conversations-header">
        <h1>
          Messages
          {totalUnread > 0 && (
            <span className="total-unread-badge">
              {totalUnread}
            </span>
          )}
        </h1>
        <button 
          className="back-button"
          onClick={() => navigate('/')}
        >
          <i className="fas fa-arrow-left"></i> Back to Properties
        </button>
      </div>

      <div className="conversations-list">
        {conversations.length === 0 ? (
          <div className="no-conversations">
            <i className="fas fa-comments"></i>
            <p>No conversations yet</p>
            <span>Start chatting with landlords!</span>
          </div>
        ) : (
          conversations.map(conversation => {
            const otherParticipant = getOtherParticipant(conversation);
            const lastMessage = conversation.last_message;

            return (
              <div 
                key={conversation.id}
                className="conversation-item"
                onClick={() => navigate(`/chat/${conversation.id}`)}
              >
                <div className="user-avatar">
                  {otherParticipant?.name?.[0]?.toUpperCase() || 'Anonymous'}
                </div>
                
                <div className="conversation-info">
                  <div className="participant-info">
                    <h3>{otherParticipant?.name || 'Unknown Landlord'}</h3>
                    {conversation.unread_count > 0 && (
                      <span className="unread-badge">
                        {conversation.unread_count}
                      </span>
                    )}
                  </div>

                  {lastMessage && (
                    <div className="last-message-container">
                      <p className="last-message">
                        {lastMessage.content.length > 50
                          ? `${lastMessage.content.substring(0, 50)}...`
                          : lastMessage.content}
                      </p>
                      <span className="message-time">
                        {formatLastMessageTime(lastMessage.timestamp)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Conversations;
